/**
 * React Query mutation hook for triggering the delete-synced-data flow for
 * an integration entity. POSTs to:
 *   /api/orgs/{orgId}/integrations/{integrationEntityId}/delete-synced-data/
 *
 * Returns a 202 with the created (or already-active) deletion jobs. On
 * success, invalidates the gmail-status query (so the integration card
 * refetches) AND the per-entity deletion-status key (the disconnected-card
 * issue polls on this key).
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiPost } from '@/lib/http/client';

export interface DeleteSyncedDataInput {
  orgId: string;
  integrationEntityId: string;
  entityTypes: string[];
}

export interface DeleteSyncedDataJob {
  id: number;
  entity_type: string;
  status: 'active' | 'completed' | 'failed' | 'cancelled';
  items_processed: number;
  items_estimated: number | null;
  items_failed: number;
  started_at: string | null;
  completed_at: string | null;
  last_progress_at: string | null;
  last_error: string;
}

export interface DeleteSyncedDataResponse {
  jobs: DeleteSyncedDataJob[];
}

async function deleteIntegrationSyncedData(
  params: DeleteSyncedDataInput
): Promise<DeleteSyncedDataResponse> {
  const url = `/api/orgs/${params.orgId}/integrations/${params.integrationEntityId}/delete-synced-data/`;
  return apiPost<DeleteSyncedDataResponse>(url, {
    entity_types: params.entityTypes,
  });
}

export function useDeleteIntegrationSyncedData() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: deleteIntegrationSyncedData,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['google', 'gmail', 'status', variables.orgId],
      });
      queryClient.invalidateQueries({
        queryKey: ['microsoft', 'outlook', 'status', variables.orgId],
      });
      queryClient.invalidateQueries({
        queryKey: [
          'deletion-status',
          variables.orgId,
          variables.integrationEntityId,
        ],
      });
    },
  });

  return {
    mutate: mutation.mutate,
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error,
    reset: mutation.reset,
    variables: mutation.variables,
  };
}
